import { NovaSpinner } from "@nova/component-nova-spinner"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import classNames from "classnames"
import {
    forwardRef,
    MouseEventHandler,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import { WebGPURenderer } from "./WebGPUMediaPlayer"
import { ColorFilter } from "./ColorFinderPanel"
import {
    isVideoFlight,
    useColorFinderImagesUrl,
    useColorFinderVideoUrl,
} from "./ColorFinderQueries"
import { useSimpleImageCache } from "./ImageCache"
import { AnomalyMediaPlayer } from "./AnomalyMediaPlayer"
import { useAnomalyIndex } from "./anomalies/useAnomalyIndex"
import { dispatchFootageEvent } from "./ColorFinderEvents"
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectColorFinderResetZoomTrigger } from "./redux/selectors"
import styles from "./ColorFinderFootageDisplay.module.css"
import { setColorFinderIsZoomed } from "./redux/slice"

interface ColorFinderFootageDisplayProps<
    TFlight = ImageColorFinderFlight | VideoColorFinderFlight,
> {
    flight: TFlight
    colorFilter: ColorFilter
    numFrames: number
    onFootageAreaMouseDown: () => void
    onFootageAreaMouseUp: () => void
    onFootageClick: FootageClickHandler
    hideAnomaly?: boolean
    anomalyOpacity: number
    anomalySensitivity: number
    hideSource?: boolean
}

export type FootageClickHandler = (context: {
    frame: number
    percentX: number
    percentY: number
}) => void

export interface MediaListenerPayload {
    frame: number
    currentTime: number
    isPlaying: boolean
    playbackSpeed: number
}

export type MediaListener = (state: MediaListenerPayload) => void

export type MediaControls = {
    type: "video" | "images"
    getFrame: () => number
    setFrame: (frame: number) => void
    getState: () => MediaListenerPayload | undefined

    play: () => Promise<void>
    pause: () => void
    setPlaybackSpeed: (speed: number) => void
    getVideoDuration: () => number
    setCurrentTime: (time: number) => void

    subscribe: (listener: MediaListener) => void
    unsubscribe: (listener: MediaListener) => void
}

export type GetMediaControls = () => MediaControls | null

const ColorFinderFootageDisplay = forwardRef<MediaControls, ColorFinderFootageDisplayProps>(
    (props, ref) => {
        return isVideoFlight(props.flight) ? (
            <ColorFinderVideo {...props} ref={ref} />
        ) : (
            <ColorFinderImages
                {...(props as ColorFinderFootageDisplayProps<ImageColorFinderFlight>)}
                ref={ref}
            />
        )
    }
)

ColorFinderFootageDisplay.displayName = "ColorFinderFootageDisplay"

const ColorFinderVideo = forwardRef<
    MediaControls,
    ColorFinderFootageDisplayProps<VideoColorFinderFlight>
>(
    (
        {
            flight,
            colorFilter,
            numFrames,
            onFootageAreaMouseDown,
            onFootageAreaMouseUp,
            onFootageClick,
            hideAnomaly: hideAnomalyProp = false,
            anomalyOpacity,
            anomalySensitivity,
            hideSource: hideSourceProp = false,
        },
        mediaControlsRef
    ) => {
        const dispatch = useAppDispatch()

        const { data: anomalyIndex } = useAnomalyIndex(flight)
        const { data: url, isLoading: urlLoading } = useColorFinderVideoUrl(flight)

        const subscriptions = useMemo(() => new Set<MediaListener>(), [])

        const [videoLoading, setVideoLoading] = useState(true)

        const videoRef = useRef<HTMLVideoElement>(null)
        const canvasRef = useRef<HTMLCanvasElement>(null)
        const anomalyCanvasRef = useRef<HTMLCanvasElement>(null)
        const rendererRef = useRef<WebGPURenderer | null>(null)
        const canvasContainerRef = useRef<HTMLDivElement>(null)
        const anomalyRendererRef = useRef<AnomalyMediaPlayer | null>(null)
        const rVFC_id = useRef<number>(undefined)

        const notifySubscribers = useCallback(() => {
            if (!videoRef.current) return

            const { duration, currentTime, paused } = videoRef.current
            const currentFrame = duration > 0 ? Math.round((currentTime / duration) * numFrames) : 0

            const state: MediaListenerPayload = {
                currentTime: currentTime,
                frame: currentFrame,
                isPlaying: !paused,
                playbackSpeed: videoRef.current.playbackRate,
            }

            for (const listener of subscriptions) {
                listener(state)
            }
            dispatchFootageEvent(state)
        }, [numFrames, subscriptions])

        const renderPausedFrame = useCallback(() => {
            const video = videoRef.current
            const renderer = rendererRef.current
            const anomalyRenderer = anomalyRendererRef.current

            const videoIsReady = video && video.readyState >= 3

            if (videoIsReady && renderer) {
                renderer.renderVideoFrame(video)
            }
            if (videoIsReady && anomalyRenderer) {
                anomalyRenderer.renderVideoFrame(video)
            }
        }, [])

        const mediaControls: MediaControls = useMemo(
            () => ({
                type: "video",
                getFrame: () => {
                    if (!videoRef.current) return -1
                    const { currentTime, duration } = videoRef.current
                    return Math.round(duration > 0 ? (currentTime / duration) * numFrames : 0)
                },
                setFrame: (frame) => {
                    if (!videoRef.current?.duration) return
                    const { duration } = videoRef.current
                    videoRef.current.currentTime = (frame / numFrames) * duration
                    renderPausedFrame()
                    notifySubscribers()
                },
                getState: () => {
                    if (!videoRef.current) return
                    const { duration, currentTime, paused } = videoRef.current
                    const currentFrame =
                        duration > 0 ? Math.round((currentTime / duration) * numFrames) : 0

                    const state: MediaListenerPayload = {
                        currentTime: currentTime,
                        frame: currentFrame,
                        isPlaying: !paused,
                        playbackSpeed: videoRef.current.playbackRate,
                    }
                    return state
                },
                subscribe: (listener) => subscriptions.add(listener),
                unsubscribe: (listener) => subscriptions.delete(listener),
                pause: () => {
                    videoRef.current?.pause()
                    notifySubscribers()
                },
                play: async () => {
                    videoRef.current?.play().then(() => {
                        notifySubscribers()
                    })
                },
                setPlaybackSpeed: (speed) => {
                    if (!videoRef.current) return
                    videoRef.current.playbackRate = speed
                    notifySubscribers()
                },
                getVideoDuration: () => {
                    if (!videoRef.current) return -1

                    return videoRef.current.duration
                },
                setCurrentTime: (time) => {
                    if (!videoRef.current) return

                    videoRef.current.currentTime = time
                    renderPausedFrame()
                    notifySubscribers()
                },
            }),
            [notifySubscribers, numFrames, subscriptions, renderPausedFrame]
        )
        useImperativeHandle(mediaControlsRef, () => mediaControls, [mediaControls])

        useEffect(() => {
            if (videoRef.current && videoRef.current.readyState >= 3) {
                setVideoLoading(false)
            }
        }, [])

        useEffect(() => {
            setTimeout(() => notifySubscribers(), 0)
        }, [notifySubscribers])

        useEffect(() => {
            if (urlLoading) {
                rendererRef.current = null
                rendererRef.current = null
                return
            }
            if (canvasRef.current && !rendererRef.current) {
                const renderer = new WebGPURenderer(canvasRef.current)
                renderer
                    .initialize()
                    .then(() => {
                        rendererRef.current = renderer
                    })
                    .catch((error) => {
                        console.error("Failed to initialize WebGPU Renderer:", error)
                    })
            }
            if (anomalyCanvasRef.current && !anomalyRendererRef.current && anomalyIndex) {
                const renderer = new AnomalyMediaPlayer(anomalyCanvasRef.current, anomalyIndex)
                renderer
                    .initialize()
                    .then(() => {
                        anomalyRendererRef.current = renderer
                    })
                    .catch((error) => {
                        console.error("Failed to initialize WebGPU Renderer:", error)
                    })
            }
        }, [anomalyIndex, urlLoading])

        useEffect(() => {
            if (rendererRef.current) {
                rendererRef.current.setColorFilter(colorFilter)
                if (videoRef.current?.paused) {
                    renderPausedFrame()
                }
            }
        }, [colorFilter, renderPausedFrame])

        useEffect(() => {
            if (anomalyRendererRef.current) {
                anomalyRendererRef.current.setSensitivity(anomalySensitivity)
                if (videoRef.current?.paused) {
                    renderPausedFrame()
                }
            }
        }, [anomalySensitivity, renderPausedFrame])

        useEffect(() => {
            if (anomalyRendererRef.current) {
                anomalyRendererRef.current.setFilterOpacity(anomalyOpacity)
                if (videoRef.current?.paused) {
                    renderPausedFrame()
                }
            }
        }, [anomalyOpacity, renderPausedFrame])

        useEffect(() => {
            const video = videoRef.current
            if (!video) return

            const updateSubscribersAndRender = () => {
                notifySubscribers()

                const renderer = rendererRef.current
                const anomalyRenderer = anomalyRendererRef.current

                if (renderer) {
                    renderer.renderVideoFrame(video)
                }
                if (anomalyRenderer) {
                    anomalyRenderer.renderVideoFrame(video)
                }
                rVFC_id.current = video.requestVideoFrameCallback(updateSubscribersAndRender)
            }

            const onPlay = () => {
                if (rVFC_id.current) {
                    video.cancelVideoFrameCallback(rVFC_id.current)
                }
                rVFC_id.current = video.requestVideoFrameCallback(updateSubscribersAndRender)
            }

            const onPauseOrEnd = () => {
                if (rVFC_id.current) {
                    video.cancelVideoFrameCallback(rVFC_id.current)
                }
                notifySubscribers()
            }

            video.addEventListener("play", onPlay)
            video.addEventListener("pause", onPauseOrEnd)
            video.addEventListener("ended", onPauseOrEnd)
            video.addEventListener("loadedmetadata", notifySubscribers)

            return () => {
                video.removeEventListener("play", onPlay)
                video.removeEventListener("pause", onPauseOrEnd)
                video.removeEventListener("ended", onPauseOrEnd)
                video.removeEventListener("loadedmetadata", notifySubscribers)
                if (rVFC_id.current) {
                    video.cancelVideoFrameCallback(rVFC_id.current)
                }
            }
        }, [notifySubscribers])

        const handleFootageClick: MouseEventHandler<HTMLCanvasElement> = (e) => {
            const canvas = e.currentTarget
            const video = videoRef.current

            if (!video || !video.videoWidth || !video.videoHeight) {
                return
            }

            const { videoWidth, videoHeight } = video

            const clickCoords = getObjectContainClickPercentage(
                e.clientX,
                e.clientY,
                canvas,
                videoWidth,
                videoHeight
            )

            if (clickCoords) {
                onFootageClick({
                    frame: mediaControls.getFrame(),
                    percentX: clickCoords.percentX,
                    percentY: clickCoords.percentY,
                })
            }
        }

        const resize = (video = videoRef.current) => {
            if (
                !video ||
                !canvasRef.current ||
                !canvasRef.current.parentElement ||
                !canvasContainerRef.current
            ) {
                return
            }

            const container = canvasRef.current.parentElement
            const canvasContainer = canvasContainerRef.current
            const { videoWidth, videoHeight } = video
            const { width: containerWidth, height: containerHeight } =
                container.getBoundingClientRect()

            if (!videoWidth || !videoHeight || !containerWidth || !containerHeight) {
                return
            }

            const isVertical = videoHeight > videoWidth
            canvasContainer.style.flexDirection = isVertical ? "row" : "column"
            canvasRef.current.width = videoWidth
            canvasRef.current.height = videoHeight

            if (anomalyCanvasRef.current && videoRef.current) {
                anomalyCanvasRef.current.width = videoWidth
                anomalyCanvasRef.current.height = videoHeight
            }
        }

        const isLoading = videoLoading || urlLoading
        const showAnomaly = !hideAnomalyProp && !!anomalyIndex
        const showSource = !hideSourceProp

        const topViewboxRef = useRef<HTMLDivElement>(null)
        const bottomViewboxRef = useRef<HTMLDivElement>(null)
        const topWrapperRef = useRef<ReactZoomPanPinchRef>(null)
        const bottomWrapperRef = useRef<ReactZoomPanPinchRef>(null)

        const zoomStateRef = useRef(false)
        const resetTrigger = useAppSelector(selectColorFinderResetZoomTrigger)

        useEffect(() => {
            setTimeout(() => {
                if (topWrapperRef.current && bottomWrapperRef.current) {
                    topWrapperRef.current.resetTransform()
                    bottomWrapperRef.current.resetTransform()
                    zoomStateRef.current = false
                }
            }, 0)
        }, [resetTrigger])

        const buildOnTransformed =
            (
                viewBoxRef: RefObject<HTMLDivElement | null>,
                viewBoxCanvasRef: RefObject<HTMLCanvasElement | null>
            ) =>
            (
                ref: ReactZoomPanPinchRef,
                state: { scale: number; positionX: number; positionY: number }
            ) => {
                const box = viewBoxRef.current
                const canvas = viewBoxCanvasRef.current
                const wrapper = ref.instance.wrapperComponent

                const isCurrentlyZoomed = state.scale > 1.05

                if (isCurrentlyZoomed !== zoomStateRef.current) {
                    zoomStateRef.current = isCurrentlyZoomed
                    dispatch(setColorFinderIsZoomed(isCurrentlyZoomed))
                }

                if (!box || !canvas || !wrapper) return

                if (state.scale <= 1.35) {
                    if (!box.classList.contains("hidden")) box.classList.add("hidden")
                    return
                } else {
                    box.classList.remove("hidden")
                }

                const wrapperRect = wrapper.getBoundingClientRect()

                const width = wrapperRect.width / state.scale
                const height = wrapperRect.height / state.scale
                const left = (state.positionX * -1) / state.scale
                const top = (state.positionY * -1) / state.scale

                box.style.width = `${width}px`
                box.style.height = `${height}px`
                box.style.left = `${left}px`
                box.style.top = `${top}px`

                box.style.boxSizing = "border-box"
                box.style.borderWidth = "2px"
                box.style.transform = "none"
                box.style.scale = "1"
            }

        const handleVideoReady = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
            setVideoLoading(false)
            resize(e.currentTarget)
            requestAnimationFrame(() => renderPausedFrame())
        }

        return (
            <div
                ref={canvasContainerRef}
                className="h-full w-full flex items-center justify-center"
                style={{ flexDirection: "column" }}
                onMouseDown={onFootageAreaMouseDown}
                onMouseUp={onFootageAreaMouseUp}
            >
                {isLoading && (
                    <NovaSpinner className="absolute left-1/2 top-1/2" height={24} width={24} />
                )}

                <div
                    className={classNames(
                        "flex-1 basis-1/2 w-full min-h-[50%]",
                        !showSource && "invisible absolute"
                    )}
                >
                    <TransformWrapper
                        ref={topWrapperRef}
                        maxScale={100}
                        onTransformed={buildOnTransformed(bottomViewboxRef, anomalyCanvasRef)}
                    >
                        <TransformComponent
                            wrapperClass="!h-full !w-full"
                            contentClass="!h-full !w-full flex items-center justify-center"
                        >
                            <canvas
                                ref={canvasRef}
                                className={classNames(
                                    { hidden: isLoading },
                                    styles.rendering_pixelated,
                                    "max-w-full max-h-full"
                                )}
                                onClick={handleFootageClick}
                            />

                            <div
                                ref={topViewboxRef}
                                className="absolute border border-blue-500 bg-white-300 pointer-events-none hidden"
                            />
                        </TransformComponent>
                    </TransformWrapper>
                </div>

                <div
                    className={classNames(
                        "flex-1 basis-1/2 w-full min-h-[50%]",
                        !showAnomaly && "invisible absolute"
                    )}
                >
                    <TransformWrapper
                        ref={bottomWrapperRef}
                        maxScale={100}
                        onTransformed={buildOnTransformed(topViewboxRef, canvasRef)}
                    >
                        <TransformComponent
                            wrapperClass="!h-full !w-full"
                            contentClass="!h-full !w-full flex items-center justify-center"
                        >
                            <canvas
                                ref={anomalyCanvasRef}
                                className={classNames(
                                    { hidden: isLoading },
                                    styles.rendering_pixelated,
                                    "max-w-full max-h-full"
                                )}
                                onClick={handleFootageClick}
                            />
                            <div
                                ref={bottomViewboxRef}
                                className="absolute border border-blue-500 bg-white-300 pointer-events-none hidden"
                            />
                        </TransformComponent>
                    </TransformWrapper>
                </div>
                <video
                    onCanPlayThrough={handleVideoReady}
                    onCanPlay={handleVideoReady}
                    onLoad={() => setVideoLoading(true)}
                    onPlaying={(e) => {
                        resize(e.currentTarget)
                    }}
                    className="absolute top-[-9999px] left-[-9999px] w-[1px] h-[1px] opacity-0 pointer-events-none"
                    src={url}
                    ref={videoRef}
                    crossOrigin={flight.demoId ? "anonymous" : "use-credentials"}
                    muted
                    playsInline
                    preload="auto"
                />
            </div>
        )
    }
)

ColorFinderVideo.displayName = "ColorFinderVideo"

const ColorFinderImages = forwardRef<
    MediaControls,
    ColorFinderFootageDisplayProps<ImageColorFinderFlight>
>(
    (
        {
            flight,
            colorFilter,
            onFootageAreaMouseDown,
            onFootageAreaMouseUp,
            onFootageClick,
            hideSource: hideSourceProp,
            hideAnomaly: hideAnomalyProp,
            anomalyOpacity,
            anomalySensitivity,
        },
        ref
    ) => {
        const images = useColorFinderImagesUrl(flight)
        const currentFrame = useRef(0)
        const canvasRef = useRef<HTMLCanvasElement>(null)
        const anomalyCanvasRef = useRef<HTMLCanvasElement>(null)
        const imageRef = useRef<HTMLImageElement>(null)
        const rendererRef = useRef<WebGPURenderer | null>(null)
        const anomalyRendererRef = useRef<AnomalyMediaPlayer | null>(null)
        const { data: anomalyIndex } = useAnomalyIndex(flight)
        const showAnomalyIndex = !hideAnomalyProp && !!anomalyIndex

        const subscriptions = useMemo(() => new Set<MediaListener>(), [])

        const notifySubscribers = useCallback(() => {
            const state: MediaListenerPayload = {
                currentTime: 0,
                frame: currentFrame.current,
                isPlaying: false,
                playbackSpeed: 0,
            }

            for (const listener of subscriptions) {
                listener(state)
            }
            dispatchFootageEvent(state)
        }, [subscriptions])

        useEffect(() => {
            setTimeout(() => notifySubscribers(), 0)
        }, [notifySubscribers])

        const { getImage, hasImage } = useSimpleImageCache(images)
        const [imageLoading, setImageLoading] = useState(false)

        const renderCurrentImage = useCallback(async () => {
            const renderer = rendererRef.current
            const anomalyRenderer = anomalyRendererRef.current
            const canvas = canvasRef.current
            const anomalyCanvas = anomalyCanvasRef.current // 1. Get reference

            // Capture the frame index we requested
            const frameToRender = currentFrame.current

            if (!renderer || !canvas) return

            const isCached = hasImage(frameToRender)
            if (!isCached) setImageLoading(true)

            try {
                const bitmap = await getImage(frameToRender)

                // Safety: If image failed or user moved slider, abort
                if (!bitmap || currentFrame.current !== frameToRender) {
                    setImageLoading(false)
                    return
                }

                // 2. Set Main Canvas Resolution
                canvas.width = bitmap.width
                canvas.height = bitmap.height

                // 3. FIX: Set Anomaly Canvas Resolution
                // If this is missing, it defaults to 300x150 (blurry)
                if (anomalyCanvas) {
                    anomalyCanvas.width = bitmap.width
                    anomalyCanvas.height = bitmap.height
                }

                // Render Main Video
                const videoFrame = new VideoFrame(bitmap, { timestamp: 0 })
                renderer.renderVideoFrame(videoFrame)
                videoFrame.close()

                // Render Anomaly
                if (anomalyRenderer) {
                    anomalyRenderer.renderImage(bitmap)
                }
            } catch (error) {
                console.error("Failed to render image:", error)
            } finally {
                setImageLoading(false)
            }
        }, [getImage, hasImage])

        useEffect(() => {
            if (canvasRef.current && !rendererRef.current) {
                const renderer = new WebGPURenderer(canvasRef.current)
                renderer
                    .initialize()
                    .then(() => {
                        rendererRef.current = renderer
                        // SET INITIAL VALUE from prop
                        renderer.setColorFilter(colorFilter)
                        // RENDER on initialize
                        renderCurrentImage()
                    })
                    .catch((error) => {
                        console.error("Failed to initialize WebGPU Renderer:", error)
                    })
            }
            if (anomalyCanvasRef.current && !anomalyRendererRef.current && anomalyIndex) {
                const renderer = new AnomalyMediaPlayer(anomalyCanvasRef.current, anomalyIndex)
                renderer
                    .initialize()
                    .then(() => {
                        anomalyRendererRef.current = renderer
                        // SET INITIAL VALUES from props
                        renderer.setSensitivity(anomalySensitivity)
                        renderer.setFilterOpacity(anomalyOpacity)
                        // RENDER on initialize
                        renderCurrentImage()
                    })
                    .catch((error) => {
                        console.error("Failed to initialize WebGPU Renderer:", error)
                    })
            }
        }, [anomalyIndex, colorFilter, anomalySensitivity, renderCurrentImage, anomalyOpacity])

        useEffect(() => {
            if (anomalyRendererRef.current) {
                anomalyRendererRef.current.setSensitivity(anomalySensitivity)
                renderCurrentImage()
            }
        }, [anomalySensitivity, renderCurrentImage])

        useEffect(() => {
            if (anomalyRendererRef.current) {
                anomalyRendererRef.current.setFilterOpacity(anomalyOpacity)
                renderCurrentImage()
            }
        }, [anomalyOpacity, renderCurrentImage])

        useEffect(() => {
            if (!imageRef.current) return

            imageRef.current.src = images[currentFrame.current]
        }, [images])

        const mediaControls: MediaControls = useMemo(
            () => ({
                type: "images",
                getFrame: () => {
                    return currentFrame.current
                },
                setFrame: (frame) => {
                    if (!imageRef.current) return
                    const cleanedFrame = Math.max(0, Math.min(images.length - 1, frame))

                    const rounded = Math.round(cleanedFrame)

                    currentFrame.current = rounded
                    imageRef.current.src = images[currentFrame.current]
                    renderCurrentImage()
                    notifySubscribers()
                },
                getState: () => {
                    if (!imageRef.current) return
                    imageRef.current.src = images[currentFrame.current]
                    return {
                        currentTime: 0,
                        frame: currentFrame.current,
                        isPlaying: false,
                        playbackSpeed: 0,
                    }
                },
                subscribe: (listener) => {
                    subscriptions.add(listener)
                },
                unsubscribe: (listener) => {
                    subscriptions.delete(listener)
                },
                pause() {},
                async play() {},
                setPlaybackSpeed: () => {},
                getVideoDuration: () => -1,
                setCurrentTime: () => {},
            }),
            [images, notifySubscribers, renderCurrentImage, subscriptions]
        )

        useImperativeHandle(ref, () => mediaControls, [mediaControls])

        useEffect(() => {
            if (rendererRef.current) {
                rendererRef.current.setColorFilter(colorFilter)
                renderCurrentImage()
            }
        }, [colorFilter, renderCurrentImage])

        useEffect(() => {
            renderCurrentImage()
        }, [images, renderCurrentImage])

        const handleFootageClick: MouseEventHandler<HTMLCanvasElement> = (e) => {
            const canvas = e.currentTarget
            const { width: intrinsicWidth, height: intrinsicHeight } = canvas

            if (!intrinsicWidth || !intrinsicHeight) {
                return
            }

            const clickCoords = getObjectContainClickPercentage(
                e.clientX,
                e.clientY,
                canvas,
                intrinsicWidth,
                intrinsicHeight
            )

            if (clickCoords) {
                onFootageClick({
                    frame: mediaControls.getFrame(),
                    percentX: clickCoords.percentX,
                    percentY: clickCoords.percentY,
                })
            }
        }

        return (
            <div
                className="relative h-full w-full flex items-center justify-center"
                style={{ flexDirection: "column" }}
                onMouseDown={onFootageAreaMouseDown}
                onMouseUp={onFootageAreaMouseUp}
            >
                {imageLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <NovaSpinner height={24} width={24} />
                    </div>
                )}
                <div
                    className={classNames(
                        "flex-1 basis-1/2 w-full min-h-[50%]",
                        hideSourceProp && "invisible absolute",
                        imageLoading && "opacity-20"
                    )}
                >
                    <TransformWrapper maxScale={100}>
                        <TransformComponent
                            wrapperClass="!h-full !w-full"
                            contentClass="!h-full !w-full"
                        >
                            <canvas
                                ref={canvasRef}
                                className={classNames(
                                    "w-full h-full object-contain",
                                    styles.rendering_pixelated
                                )}
                                onClick={handleFootageClick}
                            />
                        </TransformComponent>
                    </TransformWrapper>
                </div>

                <div
                    className={classNames(
                        "flex-1 basis-1/2 w-full min-h-[50%]",
                        !showAnomalyIndex && "invisible absolute",
                        imageLoading && "opacity-20"
                    )}
                >
                    <TransformWrapper maxScale={100}>
                        <TransformComponent
                            wrapperClass="!h-full !w-full"
                            contentClass="!h-full !w-full"
                        >
                            <canvas
                                ref={anomalyCanvasRef}
                                className={classNames(
                                    "w-full h-full object-contain",
                                    styles.rendering_pixelated
                                )}
                                onClick={handleFootageClick}
                            />
                        </TransformComponent>
                    </TransformWrapper>
                </div>

                <img
                    alt="ColorFinder image"
                    className={classNames("w-full hidden")}
                    ref={imageRef}
                    crossOrigin="use-credentials"
                />
            </div>
        )
    }
)

ColorFinderImages.displayName = "ColorFinderImages"

export { ColorFinderFootageDisplay }

interface ClickPercentage {
    percentX: number
    percentY: number
}

function getObjectContainClickPercentage(
    clientX: number,
    clientY: number,
    visualElement: HTMLElement,
    intrinsicWidth: number,
    intrinsicHeight: number
): ClickPercentage | null {
    if (!intrinsicWidth || !intrinsicHeight) {
        return null
    }

    const bounding = visualElement.getBoundingClientRect()

    const clickX = clientX - bounding.left
    const clickY = clientY - bounding.top

    const visualWidth = bounding.width
    const visualHeight = bounding.height

    const intrinsicRatio = intrinsicWidth / intrinsicHeight
    const visualRatio = visualWidth / visualHeight

    let renderedWidth: number
    let renderedHeight: number
    let offsetX: number
    let offsetY: number

    if (intrinsicRatio > visualRatio) {
        renderedWidth = visualWidth
        renderedHeight = visualWidth / intrinsicRatio
        offsetX = 0
        offsetY = (visualHeight - renderedHeight) / 2
    } else {
        renderedHeight = visualHeight
        renderedWidth = visualHeight * intrinsicRatio
        offsetY = 0
        offsetX = (visualWidth - renderedWidth) / 2
    }

    const clickOnRenderedX = clickX - offsetX
    const clickOnRenderedY = clickY - offsetY

    if (
        clickOnRenderedX < 0 ||
        clickOnRenderedX > renderedWidth ||
        clickOnRenderedY < 0 ||
        clickOnRenderedY > renderedHeight
    ) {
        return null
    }

    const percentX = clickOnRenderedX / renderedWidth
    const percentY = clickOnRenderedY / renderedHeight

    return { percentX, percentY }
}
